import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../components/ui/InputField';
import TextAreaField from '../components/ui/TextAreaField';
import Button from '../components/ui/Button';

const KlinikReportForm = ({ onDataChange }) => {
  const { t } = useTranslation();
  const [clinicInfo, setClinicInfo] = useState({
    clinicName: '',
    contactInfo: '',
    address: '',
    patientName: '',
    patientId: ''
  });

  const [services, setServices] = useState([
    { serviceName: '', date: new Date().toISOString().split('T')[0], quantity: 1, unitPrice: 0, total: 0 }
  ]);

  const [notes, setNotes] = useState('');

  // YENİ: Tax Rate (%) opsiyonel
  const [taxRate, setTaxRate] = useState(0);

  useEffect(() => {
    const subtotal = services.reduce((sum, item) => sum + item.total, 0);
    const tax = taxRate > 0 ? subtotal * (taxRate / 100) : 0;
    const grandTotal = subtotal + tax;

    onDataChange({
      ...clinicInfo,
      services,
      notes,
      subtotal,
      taxRate,
      tax,
      grandTotal,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString()
    });
  }, [clinicInfo, services, notes, taxRate, onDataChange]);

  const handleServiceChange = (index, field, value) => {
    const newServices = [...services];
    newServices[index][field] = value;
    
    if (field === 'quantity' || field === 'unitPrice') {
      newServices[index].total = newServices[index].quantity * newServices[index].unitPrice;
    }
    
    setServices(newServices);
  };

  const addService = () => {
    setServices([...services, { serviceName: '', date: new Date().toISOString().split('T')[0], quantity: 1, unitPrice: 0, total: 0 }]);
  };

  const removeService = (index) => {
    if (services.length > 1) {
      setServices(services.filter((_, i) => i !== index));
    }
  };

  const subtotal = services.reduce((sum, item) => sum + item.total, 0);
  const tax = subtotal * 0.16;
  const grandTotal = subtotal + tax;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 border border-gray-200 dark:border-gray-700 shadow-sm space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputField
          label="Clinic/Pharmacy Name"
          name="clinicName"
          value={clinicInfo.clinicName}
          onChange={(e) => setClinicInfo({ ...clinicInfo, clinicName: e.target.value })}
          placeholder="e.g., City Medical Center"
          required
        />
        <InputField
          label={t('form.contactInfo')}
          name="contactInfo"
          value={clinicInfo.contactInfo}
          onChange={(e) => setClinicInfo({ ...clinicInfo, contactInfo: e.target.value })}
          placeholder="phone@example.com"
        />
        <InputField
          label="Patient Name"
          name="patientName"
          value={clinicInfo.patientName}
          onChange={(e) => setClinicInfo({ ...clinicInfo, patientName: e.target.value })}
          placeholder="Patient full name"
        />
        <InputField
          label="Patient ID"
          name="patientId"
          value={clinicInfo.patientId}
          onChange={(e) => setClinicInfo({ ...clinicInfo, patientId: e.target.value })}
          placeholder="e.g., P-12345"
        />
        <div className="md:col-span-2">
          <TextAreaField
            label={t('form.address')}
            name="address"
            value={clinicInfo.address}
            onChange={(e) => setClinicInfo({ ...clinicInfo, address: e.target.value })}
            placeholder="Clinic address"
            rows={2}
          />
        </div>
      </div>

      <div>
        <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Services / Medications</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Service/Medication</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Date</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">{t('form.quantity')}</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">{t('form.unitPrice')}</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">{t('form.total')}</th>
                <th className="p-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {services.map((service, index) => (
                <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-2">
                    <input
                      className="form-input w-full bg-transparent border-0 focus:ring-0 p-2"
                      type="text"
                      value={service.serviceName}
                      onChange={(e) => handleServiceChange(index, 'serviceName', e.target.value)}
                      placeholder="e.g., Consultation, Paracetamol 500mg"
                    />
                  </td>
                  <td className="p-2">
                    <input
                      className="form-input w-full bg-transparent border-0 focus:ring-0 p-2"
                      type="date"
                      value={service.date}
                      onChange={(e) => handleServiceChange(index, 'date', e.target.value)}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      className="form-input w-20 bg-transparent border-0 focus:ring-0 p-2 text-center"
                      type="number"
                      min="1"
                      value={service.quantity}
                      onChange={(e) => handleServiceChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      className="form-input w-28 bg-transparent border-0 focus:ring-0 p-2 text-right"
                      type="number"
                      min="0"
                      step="0.01"
                      value={service.unitPrice}
                      onChange={(e) => handleServiceChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td className="p-2 text-right font-medium text-[#111418] dark:text-gray-100">
                    ${service.total.toFixed(2)}
                  </td>
                  <td className="p-2 text-center">
                    <button onClick={() => removeService(index)} className="text-gray-400 hover:text-red-500" disabled={services.length === 1}>
                      <span className="material-symbols-outlined text-xl">delete</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button onClick={addService} variant="ghost" icon="add_circle" className="mt-4">
            {t('form.addRow')}
          </Button>
        </div>
      </div>

      {/* Tax Rate (%) alanı */}
      <div className="mb-6">
        <InputField
          label="Tax Rate (%) - Optional"
          name="taxRate"
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={taxRate}
          onChange={e => setTaxRate(parseFloat(e.target.value) || 0)}
          placeholder="0 (No Tax)"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Leave 0 for no tax. Example: 16 (Kenya), 18 (Turkey), 5 (UAE)
        </p>
      </div>

      <div className="flex justify-end">
        <div className="w-full max-w-sm space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-gray-400">{t('form.subtotal')}</span>
            <span className="font-medium text-[#111418] dark:text-gray-100">${subtotal.toFixed(2)}</span>
          </div>
          {/* Sadece taxRate > 0 ise göster */}
          {taxRate > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">{t('form.tax')} ({taxRate}%)</span>
              <span className="font-medium text-[#111418] dark:text-gray-100">${(subtotal * (taxRate / 100)).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-lg font-bold text-[#111418] dark:text-gray-100">{t('form.grandTotal')}</span>
            <span className="text-lg font-bold text-primary">${(subtotal + (taxRate > 0 ? subtotal * (taxRate / 100) : 0)).toFixed(2)}</span>
          </div>
        </div>
      </div>

      <TextAreaField
        label={t('form.notes')}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Prescription notes, follow-up instructions..."
        rows={3}
      />
    </div>
  );
};

export default KlinikReportForm;
